import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  Table,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Button,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faExclamationTriangle,
  faBoxes,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../../axiosConfig";
import "./css/StockPage.css";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const StockPage = () => {
  const [searchParams] = useSearchParams();

  const minimum = searchParams.get("minimum");
  const lifetimeType = searchParams.get("type");

  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ================= FETCH STOCK ================= */
  const fetchStock = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        lifetime_type: "long,short",
      };

      if (minimum === "true") params.minimum = "true";
      if (lifetimeType) params.lifetime_type = lifetimeType;

      const res = await api.get("/stock-items", { params });

      setStocks(res.data || []);
      setFilteredStocks(res.data || []);
    } catch (err) {
      setError("Gagal mengambil data stock");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStock();
  }, [minimum, lifetimeType]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const keyword = search.toLowerCase();
    setFilteredStocks(
      stocks.filter(
        (item) =>
          item.part_name?.toLowerCase().includes(keyword) ||
          item.lokasi?.toLowerCase().includes(keyword),
      ),
    );
  }, [search, stocks]);

  /* ================= LOADING & ERROR ================= */
  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }
  const handleDownloadExcel = () => {
    const formattedData = filteredStocks.map((item, index) => {
      const isTools = item.lifetime_type === "tools";
      const isMinimum = !isTools && item.stock <= item.minimum_stock;

      return {
        No: index + 1,
        Kategori: item.category?.toUpperCase() || "-",
        "Nama Barang": item.part_name,
        Spesifikasi: item.spesifikasi || "-",
        Lokasi: item.lokasi || "-",
        Stock: isTools ? "-" : item.stock,
        Minimum: isTools ? "-" : item.minimum_stock,
        Status: isTools ? "TOOLS" : isMinimum ? "Stock Minimum" : "Aman",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const fileName =
      minimum === "true" ? "Stock_Minimum.xlsx" : "Data_Stock.xlsx";

    saveAs(data, fileName);
  };

  /* ================= RENDER ================= */
  return (
    <div style={{ height: "85vh" }} className="d-flex flex-column">
      <Card className="shadow-sm flex-grow-1 d-flex flex-column">
        {/* HEADER */}
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          {/* JUDUL */}
          <div className="fw-bold d-flex align-items-center">
            <FontAwesomeIcon
              icon={minimum === "true" ? faExclamationTriangle : faBoxes}
              className={
                minimum === "true" ? "text-danger me-2" : "text-primary me-2"
              }
            />
            {minimum === "true" ? "STOCK MINIMUM ITEM" : "Data Stock"}
          </div>

          {/* RIGHT SIDE */}
          <div className="d-flex align-items-center gap-2">
            <InputGroup size="sm" style={{ width: "250px" }}>
              <Form.Control
                placeholder="🔍 Cari..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
            </InputGroup>

            <Button
              size="sm"
              variant="success"
              onClick={handleDownloadExcel}
              className="d-flex align-items-center px-3"
            >
              Export Excel
            </Button>
          </div>
        </Card.Header>

        {/* BODY */}
        <Card.Body className="p-0 d-flex flex-column flex-grow-1 overflow-hidden">
          {/* WRAPPER TABEL YANG SCROLL */}
          <div className="table-scroll-wrapper">
            <Table bordered hover striped className="mb-0 stock-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Kategori</th>
                  <th>Nama Barang</th>
                  <th>Spesifikasi</th>
                  <th>Lokasi</th>
                  <th>Minimum Stock</th>
                  <th>Stock Actual</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredStocks.map((item, i) => {
                  const isTools = item.lifetime_type === "tools";
                  const isMinimum =
                    !isTools && item.stock <= item.minimum_stock;

                  return (
                    <tr
                      key={item.id}
                      className={isMinimum ? "table-danger" : ""}
                    >
                      {/* NOMOR */}
                      <td className="text-center">{i + 1}</td>

                      {/* KATEGORI */}
                      <td className="text-center">
                        <span
                          className={`badge ${
                            item.category === "tools"
                              ? "bg-secondary"
                              : item.category === "long"
                                ? "bg-primary"
                                : "bg-warning text-dark"
                          }`}
                        >
                          {item.category?.toUpperCase() || "-"}
                        </span>
                      </td>

                      <td className="cell-left">{item.part_name}</td>
                      <td className="cell-left">{item.spesifikasi || "-"}</td>
                      <td>{item.lokasi || "-"}</td>

                      {/* MINIMUM */}
                      <td className="text-center">
                        {isTools ? "-" : item.minimum_stock}
                      </td>

                      {/* STOCK */}
                      <td className="text-center">
                        {isTools ? "-" : item.stock}
                      </td>

                      {/* STATUS */}
                      <td className="text-center">
                        {isTools ? (
                          <span className="badge bg-secondary">TOOLS</span>
                        ) : isMinimum ? (
                          <span className="badge bg-danger">Stock Minimum</span>
                        ) : (
                          <span className="badge bg-success">Aman</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!filteredStocks.length && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted">
                      Tidak ada data
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default StockPage;
